import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const CannedResponses = () => {
    const [responses, setResponses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingResponse, setEditingResponse] = useState(null);
    const [formData, setFormData] = useState({
        category: '',
        title: '',
        content_ar: '',
        content_en: '',
        shortcuts: '',
        is_global: false
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchCannedResponses();
        fetchCategories();
    }, [selectedCategory, searchTerm]);

    const fetchCannedResponses = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            let url = '/api/v1/canned-responses?';
            if (selectedCategory !== 'all') {
                url += `category=${encodeURIComponent(selectedCategory)}&`;
            }
            if (searchTerm) {
                url += `search=${encodeURIComponent(searchTerm)}`;
            }

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setResponses(data.data || []);
                    setError(null);
                }
            } else if (response.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            } else {
                setError('Failed to load canned responses');
            }
        } catch (err) {
            console.error('Error fetching canned responses:', err);
            setError('Network error loading canned responses');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/api/v1/canned-responses/categories', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setCategories(data.data || []);
                }
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const handleCreateResponse = async () => {
        const token = localStorage.getItem('token');
        
        if (!formData.category || !formData.title || (!formData.content_ar && !formData.content_en)) {
            showNotification('Please fill in required fields', 'error');
            return;
        }

        try {
            const response = await fetch('/api/v1/canned-responses', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                showNotification('Canned response created successfully', 'success');
                setShowCreateModal(false);
                resetForm();
                fetchCannedResponses();
                fetchCategories();
            } else {
                showNotification(data.message || 'Failed to create response', 'error');
            }
        } catch (err) {
            console.error('Error creating response:', err);
            showNotification('Network error creating response', 'error');
        }
    };

    const handleUpdateResponse = async () => {
        const token = localStorage.getItem('token');
        
        if (!editingResponse) return;

        try {
            const response = await fetch(`/api/v1/canned-responses/${editingResponse.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                showNotification('Canned response updated successfully', 'success');
                setEditingResponse(null);
                resetForm();
                fetchCannedResponses();
            } else {
                showNotification(data.message || 'Failed to update response', 'error');
            }
        } catch (err) {
            console.error('Error updating response:', err);
            showNotification('Network error updating response', 'error');
        }
    };

    const handleDeleteResponse = async (id) => {
        if (!window.confirm('Are you sure you want to delete this response?')) {
            return;
        }

        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`/api/v1/canned-responses/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (response.ok && data.success) {
                showNotification('Response deleted successfully', 'success');
                fetchCannedResponses();
            } else {
                showNotification(data.message || 'Failed to delete response', 'error');
            }
        } catch (err) {
            console.error('Error deleting response:', err);
            showNotification('Network error deleting response', 'error');
        }
    };

    const handleUseResponse = async (response) => {
        const token = localStorage.getItem('token');

        // Increment usage count
        await fetch(`/api/v1/canned-responses/${response.id}/use`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        });

        // Copy to clipboard based on language preference
        const textToCopy = response.content_ar || response.content_en;
        navigator.clipboard.writeText(textToCopy).then(() => {
            showNotification('Response copied to clipboard!', 'success');
        });

        // Refresh to update usage count
        fetchCannedResponses();
    };

    const openEditModal = (response) => {
        setEditingResponse(response);
        setFormData({
            category: response.category,
            title: response.title,
            content_ar: response.content_ar || '',
            content_en: response.content_en || '',
            shortcuts: response.shortcuts || '',
            is_global: response.is_global
        });
    };

    const resetForm = () => {
        setFormData({
            category: '',
            title: '',
            content_ar: '',
            content_en: '',
            shortcuts: '',
            is_global: false
        });
    };

    const showNotification = (message, type) => {
        const notification = document.createElement('div');
        notification.textContent = message;
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 15px 20px;
            background-color: ${type === 'success' ? '#28a745' : '#dc3545'};
            color: white;
            border-radius: 5px;
            z-index: 10000;
            animation: slideIn 0.3s ease-out;
        `;
        document.body.appendChild(notification);

        setTimeout(() => {
            notification.style.animation = 'slideOut 0.3s ease-out';
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    };

    const getCategoryBadgeColor = (category) => {
        const colors = {
            'greeting': '#007bff',
            'thanks': '#28a745',
            'technical': '#6c757d',
            'billing': '#ffc107',
            'support': '#17a2b8',
            'closing': '#6f42c1'
        };
        return colors[category] || '#6c757d';
    };

    return (
        <Layout>
            <div style={{ padding: '20px' }}>
                {/* Header */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '30px'
                }}>
                    <div>
                        <h1 style={{ margin: '0 0 10px 0' }}>Canned Responses</h1>
                        <p style={{ margin: 0, color: '#6c757d' }}>Quick responses for common customer queries</p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '500'
                        }}
                    >
                        + Create New Response
                    </button>
                </div>

                {/* Filters */}
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '300px 1fr',
                    gap: '20px',
                    marginBottom: '30px'
                }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Category
                        </label>
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '5px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Search
                        </label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by title or content..."
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '5px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                </div>

                {/* Loading/Error States */}
                {loading && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <p>Loading canned responses...</p>
                    </div>
                )}

                {error && (
                    <div style={{
                        padding: '20px',
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        borderRadius: '5px',
                        marginBottom: '20px'
                    }}>
                        {error}
                    </div>
                )}

                {/* Responses List */}
                {!loading && !error && (
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        padding: '20px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
                            Saved Responses ({responses.length})
                        </h2>

                        {responses.length === 0 ? (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '60px 20px',
                                color: '#6c757d' 
                            }}>
                                <h3>No canned responses found</h3>
                                <p style={{ color: '#6c757d' }}>Create your first canned response to speed up your customer service!</p>
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        marginTop: '20px'
                                    }}
                                >
                                    Create First Response
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gap: '15px' }}>
                                {responses.map(response => (
                                    <div 
                                        key={response.id}
                                        style={{
                                            padding: '20px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            borderLeft: `4px solid ${getCategoryBadgeColor(response.category)}`
                                        }}
                                    >
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginBottom: '15px'
                                        }}>
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                                    <h3 style={{ margin: 0 }}>{response.title}</h3>
                                                    <span style={{
                                                        padding: '4px 12px',
                                                        backgroundColor: getCategoryBadgeColor(response.category),
                                                        color: 'white',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '500'
                                                    }}>
                                                        {response.category}
                                                    </span>
                                                    {response.is_global && (
                                                        <span style={{
                                                            padding: '4px 12px',
                                                            backgroundColor: '#17a2b8',
                                                            color: 'white',
                                                            borderRadius: '12px',
                                                            fontSize: '12px',
                                                            fontWeight: '500'
                                                        }}>
                                                            Global
                                                        </span>
                                                    )}
                                                </div>
                                                {response.shortcuts && (
                                                    <div style={{ fontSize: '13px', color: '#6c757d' }}>
                                                        Shortcut: /{response.shortcuts}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                                                    Used {response.usage_count || 0} times
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <button
                                                    onClick={() => handleUseResponse(response)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        backgroundColor: '#28a745',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '5px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Use
                                                </button>
                                                <button
                                                    onClick={() => openEditModal(response)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        backgroundColor: '#ffc107',
                                                        color: '#212529',
                                                        border: 'none',
                                                        borderRadius: '5px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteResponse(response.id)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        backgroundColor: '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '5px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>

                                        {/* Content Preview */}
                                        <div style={{ display: 'grid', gap: '10px' }}>
                                            {response.content_ar && (
                                                <div style={{
                                                    padding: '12px',
                                                    backgroundColor: 'white',
                                                    borderRadius: '5px',
                                                    direction: 'rtl',
                                                    textAlign: 'right'
                                                }}>
                                                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px' }}>
                                                        Arabic
                                                    </div>
                                                    <div style={{ fontSize: '14px' }}>
                                                        {response.content_ar}
                                                    </div>
                                                </div>
                                            )}
                                            {response.content_en && (
                                                <div style={{
                                                    padding: '12px',
                                                    backgroundColor: 'white',
                                                    borderRadius: '5px'
                                                }}>
                                                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px' }}>
                                                        English
                                                    </div>
                                                    <div style={{ fontSize: '14px' }}>
                                                        {response.content_en}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Create/Edit Modal */}
                {(showCreateModal || editingResponse) && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            padding: '30px',
                            borderRadius: '10px',
                            width: '90%',
                            maxWidth: '600px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}>
                            <h2 style={{ marginTop: 0 }}>
                                {editingResponse ? 'Edit Canned Response' : 'Create New Canned Response'}
                            </h2>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Category *
                                </label>
                                <input
                                    type="text"
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    list="categories"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px'
                                    }}
                                />
                                <datalist id="categories">
                                    {categories.map(cat => (
                                        <option key={cat} value={cat} />
                                    ))}
                                </datalist>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Arabic Content
                                </label>
                                <textarea
                                    value={formData.content_ar}
                                    onChange={(e) => setFormData({ ...formData, content_ar: e.target.value })}
                                    rows="4"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        direction: 'rtl'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    English Content
                                </label>
                                <textarea
                                    value={formData.content_en}
                                    onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                                    rows="4"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Shortcut (e.g., greeting, thanks)
                                </label>
                                <input
                                    type="text"
                                    value={formData.shortcuts}
                                    onChange={(e) => setFormData({ ...formData, shortcuts: e.target.value })}
                                    placeholder="Type / followed by this to quickly insert"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px'
                                    }}
                                />
                            </div>

                            {!editingResponse && (
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.is_global}
                                            onChange={(e) => setFormData({ ...formData, is_global: e.target.checked })}
                                            style={{ marginRight: '10px' }}
                                        />
                                        Make this a global response (visible to all agents)
                                    </label>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setEditingResponse(null);
                                        resetForm();
                                    }}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={editingResponse ? handleUpdateResponse : handleCreateResponse}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {editingResponse ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default CannedResponses;